import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AnimatedLogoLoader from './AnimatedLogoLoader';

interface AuthLoadingScreenProps {
  minLoadTime?: number;
}

/**
 * Component that shows a loading screen after user authentication
 * It tracks auth state changes and shows a loading screen when the user signs in
 */
const AuthLoadingScreen: React.FC<AuthLoadingScreenProps> = ({ 
  minLoadTime = 1800, // Show for at least 1.8 seconds after login
}) => {
  const { user, isLoading: authLoading } = useAuth();
  const [showLoading, setShowLoading] = useState(false);
  const [prevUser, setPrevUser] = useState<any>(null);
  const [prevAuthLoading, setPrevAuthLoading] = useState<boolean>(true);
  
  useEffect(() => {
    // Track when auth state changes from loading to not loading with a user
    // This captures OAuth redirects and direct logins
    if (prevAuthLoading && !authLoading && user) {
      setShowLoading(true);
    }
    
    // If we went from no user to having a user, it means we just logged in
    if (!prevUser && user) {
      setShowLoading(true);
    }
    
    // Update previous states for comparison on next render
    setPrevUser(user);
    setPrevAuthLoading(authLoading);
  }, [user, prevUser, authLoading, prevAuthLoading]);
  
  useEffect(() => {
    // If loading screen is showing, wait for content to load then hide
    if (showLoading) {
      // Wait for page content to be loaded
      const checkContentLoaded = () => {
        // If document is already loaded, set a timer to hide the loader
        if (document.readyState === 'complete') {
          const timer = setTimeout(() => {
            setShowLoading(false);
          }, minLoadTime);
          
          return () => clearTimeout(timer);
        } else {
          // Wait for the load event
          const handleLoad = () => {
            const timer = setTimeout(() => {
              setShowLoading(false);
            }, minLoadTime);
            
            window.removeEventListener('load', handleLoad);
            return () => clearTimeout(timer);
          };
          
          window.addEventListener('load', handleLoad);
          return () => window.removeEventListener('load', handleLoad);
        }
      };
      
      return checkContentLoaded();
    }
  }, [showLoading, minLoadTime]);
  
  return <AnimatedLogoLoader isLoading={showLoading} minDisplayTime={minLoadTime} />;
};

export default AuthLoadingScreen;